import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../redux/helpers/axios";

/**
 * @author
 * @function ElevenOrders
 *
 * Admin: 11.11 orders (one row per product line). Update status (single or
 * bulk), record cash collected on delivery, and see the full history.
 **/

const API = {
  list: "/eleven/admin/orders",
  one: (id) => `/eleven/admin/orders/${id}`,
  status: (id) => `/eleven/admin/orders/${id}/status`,
  bulk: "/eleven/admin/orders/bulk-status",
  cod: (id) => `/eleven/admin/orders/${id}/collect-cod`,
};

// order matters: this is also the workflow order in the status picker
const STATUSES = [
  ["PENDING", "Pending payment", "bg-amber-100 text-amber-800"],
  ["APPROVED", "Approved", "bg-indigo-100 text-indigo-700"],
  ["PROCESSING", "Processing", "bg-indigo-100 text-indigo-700"],
  ["PURCHASED", "Purchased", "bg-indigo-100 text-indigo-700"],
  ["IN_WAREHOUSE_CN", "China warehouse", "bg-sky-100 text-sky-800"],
  ["IN_TRANSIT", "In transit", "bg-sky-100 text-sky-800"],
  ["IN_WAREHOUSE_BD", "BD warehouse", "bg-sky-100 text-sky-800"],
  ["DELIVERY_PROCESSING", "Delivery prep", "bg-violet-100 text-violet-800"],
  ["HANDOVER_TO_COURIER", "With courier", "bg-violet-100 text-violet-800"],
  ["DELIVERED", "Delivered", "bg-emerald-100 text-emerald-800"],
  ["CANCELLED", "Cancelled", "bg-gray-200 text-gray-700"],
  ["REFUNDED", "Refunded", "bg-gray-200 text-gray-700"],
];
const STATUS = Object.fromEntries(
  STATUSES.map(([k, label, cls]) => [k, { label, cls }]),
);

const PAYMENT = {
  UNPAID: { label: "Unpaid", cls: "text-red-600" },
  PARTIALLY_PAID: { label: "Advance paid", cls: "text-emerald-700" },
  PAID: { label: "Fully paid", cls: "text-emerald-700" },
  REFUNDED: { label: "Refunded", cls: "text-gray-600" },
  PARTIALLY_REFUNDED: { label: "Partly refunded", cls: "text-gray-600" },
  CANCELLED: { label: "Cancelled", cls: "text-gray-500" },
};

const ZONE = { INSIDE_DHAKA: "Inside Dhaka", OUTSIDE_DHAKA: "Outside Dhaka" };

const formatPrice = (n) =>
  new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/* ---------- small UI ---------- */

const Badge = ({ status }) => {
  const s = STATUS[status] || {
    label: status,
    cls: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}
    >
      {s.label}
    </span>
  );
};

const Img = ({ src, className = "" }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (!src || broken)
    return (
      <div
        className={`bg-gray-100 rounded-md border border-gray-200 ${className}`}
      />
    );
  return (
    <img
      referrerPolicy="no-referrer"
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className={`object-cover rounded-md border border-gray-200 ${className}`}
    />
  );
};

const Copy = ({ value }) => {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(String(value));
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch {
          /* clipboard blocked */
        }
      }}
      className="text-[11px] font-medium text-indigo-600 hover:underline"
    >
      {done ? "Copied" : "Copy"}
    </button>
  );
};

const Pagination = ({ p, onPage, disabled }) => {
  if (!p || p.totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 text-sm">
      <span className="text-gray-500">
        Page <b className="text-gray-800">{p.currentPage}</b> of {p.totalPages}{" "}
        · {p.total} total
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled || !p.hasPreviousPage}
          onClick={() => onPage(p.currentPage - 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          ← Prev
        </button>
        <button
          type="button"
          disabled={disabled || !p.hasNextPage}
          onClick={() => onPage(p.currentPage + 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className="fixed z-[70] top-4 right-4 max-w-sm">
      <div
        className={`rounded-lg px-4 py-3 shadow-lg text-sm text-white ${toast.type === "error" ? "bg-red-600" : "bg-gray-900"}`}
      >
        {toast.msg}
      </div>
    </div>
  );
};

const Drawer = ({ open, onClose, title, children }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-gray-50 shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500";

const StatusSelect = ({
  value,
  onChange,
  exclude,
  placeholder = "Select status",
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={inputCls}
  >
    <option value="">{placeholder}</option>
    {STATUSES.filter(([k]) => k !== exclude).map(([k, label]) => (
      <option key={k} value={k}>
        {label}
      </option>
    ))}
  </select>
);

/* ---------- order detail ---------- */

const OrderDetail = ({ id, onChanged, notify }) => {
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(API.one(id));
      setO(data.order);
    } catch (err) {
      notify("error", err.response?.data?.msg || "Failed to load order");
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (e) => {
    e.preventDefault();
    if (!next) return;
    if (
      ["CANCELLED", "REFUNDED"].includes(next) &&
      !window.confirm(`Mark this order as ${STATUS[next].label}?`)
    )
      return;
    setSaving("status");
    try {
      const { data } = await axiosInstance.patch(API.status(id), {
        status: next,
        msg: msg.trim() || undefined,
      });
      notify("success", data.msg);
      setNext("");
      setMsg("");
      await load();
      onChanged();
    } catch (err) {
      notify("error", err.response?.data?.msg || "Failed to update status");
    } finally {
      setSaving(null);
    }
  };

  const collectCod = async () => {
    if (
      !window.confirm(
        `Mark ${formatPrice(o.totalDue)} as collected in cash for ${o.orderNo}?`,
      )
    )
      return;
    setSaving("cod");
    try {
      const { data } = await axiosInstance.post(API.cod(id), {});
      notify("success", data.msg);
      await load();
      onChanged();
    } catch (err) {
      notify("error", err.response?.data?.msg || "Failed to record payment");
    } finally {
      setSaving(null);
    }
  };

  if (loading && !o) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!o) return null;

  const s = o.checkoutSession;
  const a = o.deliveryAddress;
  const pay = PAYMENT[o.paymentStatus] || {
    label: o.paymentStatus,
    cls: "text-gray-600",
  };
  const closed = ["CANCELLED", "REFUNDED"].includes(o.status);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="font-mono text-sm font-bold text-gray-900">
            {o.orderNo} <Copy value={o.orderNo} />
          </p>
          <Badge status={o.status} />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Placed {formatDate(o.createdAt)}
        </p>

        <div className="mt-3 flex gap-3">
          <Img
            src={o.variationImg || o.productImg}
            className="w-20 h-20 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              {o.productTitle}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {o.variationName ? `${o.variationName} · ` : ""}
              {o.sku ? (
                <>
                  SKU {o.sku} <Copy value={o.sku} /> ·{" "}
                </>
              ) : (
                ""
              )}
              PID {o.product?.pid || "—"} <Copy value={o.product?.pid} />
            </p>
            <p className="text-sm text-gray-700 mt-1">
              {o.totalQty} × {formatPrice(o.unitPrice)} ={" "}
              <b className="text-gray-900">{formatPrice(o.itemTotal)}</b>
            </p>
            {o.product?.productUrl && (
              <a
                href={o.product.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-1 text-xs font-semibold text-indigo-600 hover:underline"
              >
                Open supplier page (
                {o.product.store === "A1688" ? "1688" : o.product.store}) ↗
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Payment */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Payment</h3>
          <span className={`text-xs font-semibold ${pay.cls}`}>
            {pay.label}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
            <p className="text-[11px] font-bold uppercase text-emerald-700">
              Paid
            </p>
            <p className="text-lg font-extrabold text-emerald-800">
              {formatPrice(o.totalPaid)}
            </p>
          </div>
          <div
            className={`rounded-lg px-3 py-2 border ${Number(o.totalDue) > 0 ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`}
          >
            <p className="text-[11px] font-bold uppercase text-amber-700">
              Product due
            </p>
            <p className="text-lg font-extrabold text-amber-900">
              {formatPrice(o.totalDue)}
            </p>
          </div>
        </div>
        {s && (
          <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 space-y-1">
            <p>
              Checkout{" "}
              <span className="font-mono">{s.sessionId.slice(0, 8)}…</span>{" "}
              <Copy value={s.sessionId} /> · {s._count?.orders} order(s) ·{" "}
              {s.status}
            </p>
            <p>
              Advance {formatPrice(s.paidAmount)} of{" "}
              {formatPrice(s.requestedAmount)}
              {s.gatewayTrxId ? ` · trx ${s.gatewayTrxId}` : ""}
            </p>
            <p>
              <b className="text-gray-800">
                Cash on delivery for whole checkout: {formatPrice(s.codAmount)}
              </b>{" "}
              (delivery{" "}
              {Number(s.deliveryFee) > 0 ? formatPrice(s.deliveryFee) : "free"}
              {s.deliveryZone
                ? `, ${ZONE[s.deliveryZone] || s.deliveryZone}`
                : ""}
              )
            </p>
          </div>
        )}
        {!closed && Number(o.totalDue) > 0 && o.paymentStatus !== "UNPAID" && (
          <button
            type="button"
            onClick={collectCod}
            disabled={saving === "cod"}
            className="mt-3 w-full px-4 py-2 text-sm font-semibold rounded-lg border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-60"
          >
            {saving === "cod"
              ? "Saving…"
              : `Mark ${formatPrice(o.totalDue)} collected (cash on delivery)`}
          </button>
        )}
      </div>

      {/* Customer + address */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Customer account</p>
          <p className="text-sm font-semibold text-gray-900">{o.user?.name}</p>
          <p className="text-xs text-gray-600 break-all">{o.user?.email}</p>
          {o.user?.phone && (
            <p className="text-xs text-gray-600">{o.user.phone}</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Deliver to</p>
          {a ? (
            <>
              <p className="text-sm font-semibold text-gray-900">{a.name}</p>
              <p className="text-xs text-gray-700">
                {a.phone} <Copy value={a.phone} />
              </p>
              <p className="text-xs text-gray-600">
                {a.street}, {a.city}, {a.country}{" "}
                <Copy value={`${a.name}, ${a.phone}, ${a.street}, ${a.city}`} />
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-400">—</p>
          )}
        </div>
      </div>

      {/* Update status */}
      <form
        onSubmit={updateStatus}
        className="bg-white rounded-xl border border-gray-200 p-4 space-y-3"
      >
        <h3 className="text-sm font-semibold text-gray-800">Update status</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <StatusSelect
            value={next}
            onChange={setNext}
            exclude={o.status}
            placeholder="New status"
          />
          <input
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="Note for the customer (optional)"
            className={inputCls}
          />
        </div>
        <button
          type="submit"
          disabled={!next || saving === "status"}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving === "status" ? "Saving…" : "Update status"}
        </button>
      </form>

      {/* History */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">History</h3>
        <ol className="relative border-l-2 border-gray-200 ml-1.5 space-y-4">
          {o.history.map((h, i) => (
            <li key={h.id} className="ml-4">
              <span
                className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 border-white ${i === 0 ? "bg-indigo-600" : "bg-gray-300"}`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-gray-900">
                  {h.statusValue}
                </p>
                <Badge status={h.status} />
              </div>
              {h.msg && <p className="text-xs text-gray-600 mt-0.5">{h.msg}</p>}
              <p className="text-[11px] text-gray-400 mt-0.5">
                {formatDate(h.createdAt)}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

/* ---------- page ---------- */

export const ElevenOrders = (props) => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    paymentStatus: "",
    from: "",
    to: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState({ status: "", msg: "" });
  const [bulkSaving, setBulkSaving] = useState(false);

  const notify = useCallback((type, msg) => setToast({ type, msg }), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get(API.list, {
        params: {
          page,
          status: status || undefined,
          paymentStatus: filters.paymentStatus || undefined,
          search: filters.search || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
        },
      });
      setRows(data.orders || []);
      setCounts(data.stats?.byStatus || {});
      setPagination(data.pagination);
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [page, status, filters]);

  useEffect(() => {
    load();
  }, [load]);

  // clear selection whenever the visible list changes
  useEffect(() => setSelected([]), [page, status, filters]);

  const allCount = Object.values(counts).reduce((n, c) => n + c, 0);
  const allOnPage =
    rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const toggle = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );
  const toggleAll = () => setSelected(allOnPage ? [] : rows.map((r) => r.id));

  const applyBulk = async () => {
    if (!bulk.status || !selected.length) return;
    if (
      !window.confirm(
        `Set ${selected.length} order(s) to "${STATUS[bulk.status].label}"?`,
      )
    )
      return;
    setBulkSaving(true);
    try {
      const { data } = await axiosInstance.patch(API.bulk, {
        ids: selected,
        status: bulk.status,
        msg: bulk.msg.trim() || undefined,
      });
      notify("success", data.msg);
      setSelected([]);
      setBulk({ status: "", msg: "" });
      load();
    } catch (err) {
      notify("error", err.response?.data?.msg || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] content-start">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Orders</h1>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        {/* Status tabs */}
        <div className="flex gap-2 overflow-x-auto px-3 pt-3 pb-1">
          {[["", "All"], ...STATUSES.map(([k, label]) => [k, label])].map(
            ([k, label]) => (
              <button
                key={k || "all"}
                type="button"
                onClick={() => {
                  setPage(1);
                  setStatus(k);
                }}
                className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold border ${
                  status === k
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                }`}
              >
                {label}
                <span
                  className={`ml-1.5 text-xs ${status === k ? "text-white/80" : "text-gray-400"}`}
                >
                  {k ? (counts[k] ?? 0) : allCount}
                </span>
              </button>
            ),
          )}
        </div>

        {/* Filters */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setFilters((f) => ({ ...f, search: search.trim() }));
          }}
          className="flex flex-col xl:flex-row gap-2 px-3 py-3 border-b border-gray-100"
        >
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Order no., product, SKU, customer, phone, session"
            className={`${inputCls} xl:w-96`}
          />
          <select
            value={filters.paymentStatus}
            onChange={(e) => {
              setPage(1);
              setFilters((f) => ({ ...f, paymentStatus: e.target.value }));
            }}
            className={`${inputCls} xl:w-48`}
          >
            <option value="">Any payment</option>
            {Object.entries(PAYMENT).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, from: e.target.value }));
              }}
              className={inputCls}
              aria-label="From date"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, to: e.target.value }));
              }}
              className={inputCls}
              aria-label="To date"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Search
          </button>
        </form>

        {/* Bulk bar */}
        {selected.length > 0 && (
          <div className="flex flex-col md:flex-row md:items-center gap-2 px-3 py-3 bg-indigo-50 border-b border-indigo-100">
            <span className="text-sm font-semibold text-indigo-900 shrink-0">
              {selected.length} selected
            </span>
            <div className="md:w-52">
              <StatusSelect
                value={bulk.status}
                onChange={(v) => setBulk((b) => ({ ...b, status: v }))}
                placeholder="Set status to…"
              />
            </div>
            <input
              value={bulk.msg}
              onChange={(e) => setBulk((b) => ({ ...b, msg: e.target.value }))}
              placeholder="Note for customers (optional), e.g. Shipment GZ-12"
              className={`${inputCls} md:flex-1`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={applyBulk}
                disabled={!bulk.status || bulkSaving}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {bulkSaving ? "Updating…" : "Apply"}
              </button>
              <button
                type="button"
                onClick={() => setSelected([])}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {error ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 text-sm font-semibold text-indigo-600 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={allOnPage}
                      onChange={toggleAll}
                      aria-label="Select all on this page"
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th className="px-3 py-2.5 font-medium">Order</th>
                  <th className="px-3 py-2.5 font-medium">Product</th>
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium text-right">Amount</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-10 text-center text-gray-400"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-10 text-center text-gray-400"
                    >
                      No orders found.
                    </td>
                  </tr>
                ) : (
                  rows.map((o) => {
                    const pay = PAYMENT[o.paymentStatus] || {
                      label: o.paymentStatus,
                      cls: "text-gray-600",
                    };
                    return (
                      <tr
                        key={o.id}
                        className={`border-t border-gray-100 hover:bg-gray-50 ${selected.includes(o.id) ? "bg-indigo-50/50" : ""} ${loading ? "opacity-60" : ""}`}
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selected.includes(o.id)}
                            onChange={() => toggle(o.id)}
                            aria-label={`Select ${o.orderNo}`}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <p className="font-mono text-xs font-semibold text-gray-900">
                            {o.orderNo}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {formatDate(o.createdAt)}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2 w-44 xl:w-56">
                            <Img
                              src={o.variationImg || o.productImg}
                              className="w-9 h-9 shrink-0"
                            />
                            <div className="min-w-0">
                              <p
                                className="text-gray-900 truncate"
                                title={o.productTitle}
                              >
                                {o.productTitle}
                              </p>
                              <p
                                className="text-xs text-gray-500 truncate"
                                title={o.variationName || undefined}
                              >
                                Qty {o.totalQty}
                                {o.variationName ? ` · ${o.variationName}` : ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="w-32 xl:w-40">
                            <p className="font-medium text-gray-900 truncate">
                              {o.deliveryAddress?.name || o.user?.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {o.deliveryAddress?.phone || o.user?.phone}
                              {o.deliveryAddress?.city
                                ? ` · ${o.deliveryAddress.city}`
                                : ""}
                            </p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <p className="font-bold text-gray-900">
                            {formatPrice(o.itemTotal)}
                          </p>
                          <p className="text-xs">
                            <span className="text-emerald-700 font-semibold">
                              {formatPrice(o.totalPaid)}
                            </span>
                            <span className="text-gray-300 mx-1">/</span>
                            <span className="text-amber-700 font-semibold">
                              {formatPrice(o.totalDue)}
                            </span>
                          </p>
                          <p className={`text-[11px] font-semibold ${pay.cls}`}>
                            {pay.label}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <Badge status={o.status} />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setOpenId(o.id)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination p={pagination} onPage={setPage} disabled={loading} />
      </div>

      <Drawer
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title="Order details"
      >
        {openId && <OrderDetail id={openId} onChanged={load} notify={notify} />}
      </Drawer>
    </div>
  );
};

export default ElevenOrders;
